import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { TypeaheadSearchFunction } from '@app/shared/shared-api.model';
import { TranslateFn, translateWithDefaults } from '@app/shared/utils';
import { IconComponent } from '@degreed/apollo-angular';
import { DfSpinnerModule } from '@lib/fresco';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { UserSearchItem } from '@app/user/user-api.model';
import { CdkScrollableModule, CdkScrollable } from '@angular/cdk/scrolling';
import { UserSearchItemV3Component } from './user-search-item-v3/user-search-item-v3.component';

@Component({
    selector: 'dgx-user-search-v3',
    imports: [
        IconComponent,
        DfSpinnerModule,
        UserSearchItemV3Component,
        CdkScrollableModule,
    ],
    templateUrl: './user-search-v3.component.html',
    styles: [
        `
      input[type='search']::-webkit-search-cancel-button {
        display: none;
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSearchV3Component {
  @ViewChild('searchInput') public searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') public dropDown!: ElementRef<HTMLUListElement>;
  @ViewChild(CdkScrollable) scrollable!: CdkScrollable;

  @Input({ required: true })
  public searchFn!: TypeaheadSearchFunction<string, UserSearchItem>;
  @Input({ required: true }) public ariaLabel: string;
  @Input({ required: true }) public placeHolderLabel: string;

  @Input() public searchQuery: Observable<string>;

  /** The event emitted upon selection. */
  @Output()
  public onSelect: EventEmitter<UserSearchItem> = new EventEmitter();

  public isOpen = signal<boolean>(false);
  public activeIndex = signal<number>(-1);
  public isLoading = signal<boolean>(false);
  public showClearButton = signal<boolean>(false);
  public searchResults = signal<readonly UserSearchItem[]>([]);
  public translate: TranslateFn;

  constructor(public translateService: TranslateService) {
    this.translate = translateWithDefaults(translateService);
  }

  public onDropdownKeyDown(event: KeyboardEvent) {
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Enter' ||
      event.key === 'Escape'
    ) {
      this.onSearchQuery(event);
    } else if (event.key === 'Tab') {
      this.onEnterKey(event);
    }
  }

  public onSearchQuery(event: KeyboardEvent) {
    const query = this.searchInput.nativeElement.value;
    this.searchQuery = of(query);
    // If no query
    if (!query.length) {
      this.activeIndex.set(-1);
      this.isOpen.set(false);
      this.searchResults.set([]);
      this.showClearButton.set(false);
      return;
    }

    // Query exists
    if (!this.isOpen()) {
      this.isOpen.set(true);
      this.showClearButton.set(true);
    }

    const length = this.searchResults().length;
    if (!length) {
      // No results yet, fetch data
      this.activeIndex.set(-1);
      this.isLoading.set(true);
      this.executeSearch();
      return;
    }
    // We have existing results
    switch (event.key) {
      case 'ArrowDown':
        this.onDownArrow(event);
        break;
      case 'ArrowUp':
        this.onUpArrow(event);
        break;
      case 'Enter':
        this.onEnterKey(event);
        break;
      case 'Escape':
        this.onEscapeKey(event);
        break;
      case 'Tab':
        this.isOpen.set(false);
        break;
      default:
        // Any other key: fetch new data
        this.activeIndex.set(-1);
        this.isLoading.set(true);
        this.executeSearch();
        break;
    }
  }

  public onFocus() {
    if (!this.isOpen() && this.searchResults().length) {
      this.isOpen.set(true);
    }
  }

  public onItemSelect(item: UserSearchItem) {
    this.clearSearch();
    this.onSelect.emit(item);
  }

  public clearSearch() {
    this.searchInput.nativeElement.value = '';
    this.searchQuery = of('');
    this.searchResults.set([]);
    this.isOpen.set(false);
    this.showClearButton.set(false);
  }

  public onEscape(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.onEscapeKey(event);
    }
  }

  private onDownArrow(event: KeyboardEvent) {
    event.preventDefault();
    const items =
      this.dropDown.nativeElement.querySelectorAll<HTMLElement>(
        'li[role="option"]'
      );
    this.activeIndex.set((this.activeIndex() + 1) % items.length);
    this.scrollToIndex();
    items[this.activeIndex()].focus();
  }

  private onUpArrow(event: KeyboardEvent) {
    event.preventDefault();
    const items =
      this.dropDown.nativeElement.querySelectorAll<HTMLElement>(
        'li[role="option"]'
      );
    this.activeIndex.set(
      (this.activeIndex() + items.length - 1) % items.length
    );
    this.scrollToIndex();
    items[this.activeIndex()].focus();
  }

  private onEnterKey(event: KeyboardEvent) {
    event.preventDefault();
    if (this.activeIndex() >= 0) {
      this.onItemSelect(this.searchResults()[this.activeIndex()]);
    }
    this.isOpen.set(false);
  }

  private onEscapeKey(event: KeyboardEvent) {
    event.preventDefault();
    this.isOpen.set(false);
  }

  private executeSearch() {
    this.searchFn(this.searchQuery).subscribe((res) => {
      if (res.length) this.isOpen.set(true);
      this.isLoading.set(false);
      this.searchResults.set(res);
    });
  }

  private scrollToIndex() {
    const items =
      this.dropDown.nativeElement.querySelectorAll<HTMLElement>(
        'li[role="option"]'
      );
    const itemHeight = items[this.activeIndex()].offsetHeight;
    this.scrollable?.scrollTo({
      top: this.activeIndex() * itemHeight,
      behavior: 'smooth',
    });
  }
}
